"use client";

import type { ReactNode } from "react";
import * as React from "react";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from "@/components/ui/select";
import { cn } from "@/utils/lib";

export interface Option {
  value: string;
  label: string;
  disable?: boolean;
  /** Fixed options stay selected and cannot be toggled off. */
  fixed?: boolean;
  /** Group the options by providing key. */
  [key: string]: string | boolean | undefined;
}

interface GroupOption {
  [key: string]: Option[];
}

type MultipleSelectorInputProps = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  "className"
>;

type MultipleSelectorCommandProps = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  "className"
>;

interface MultipleSelectorProps {
  value?: Option[];
  defaultOptions?: Option[];
  /** Manually controlled options. */
  options?: Option[];
  placeholder?: string;
  /** Loading component. */
  loadingIndicator?: ReactNode;
  /** Empty component. */
  emptyIndicator?: ReactNode;
  /** Debounce time for async search. Kept for compatibility with old callers. */
  delay?: number;
  /**
   * When `onSearch` exists, fetch the initial option list as the select opens.
   */
  triggerSearchOnFocus?: boolean;
  /** Async option loader. Text search is intentionally not part of Select. */
  onSearch?: (value: string) => Promise<Option[]>;
  onChange?: (options: Option[]) => void;
  /** Limit the maximum number of selected options. */
  maxSelected?: number;
  /** Called when the number of selected options exceeds the limit. */
  onMaxSelected?: (maxLimit: number) => void;
  /** Hide the placeholder when there are options selected. */
  hidePlaceholderWhenSelected?: boolean;
  disabled?: boolean;
  /** Group the options based on the provided key. */
  groupBy?: string;
  className?: string;
  badgeClassName?: string;
  /** Compatibility-only. Base UI Select does not auto-select list items. */
  selectFirstItem?: boolean;
  /** Compatibility-only. Select options must come from the provided list. */
  creatable?: boolean;
  /** Compatibility-only wrapper props from the previous implementation. */
  commandProps?: MultipleSelectorCommandProps;
  /** Compatibility-only input props from the previous implementation. */
  inputProps?: MultipleSelectorInputProps;
}

export interface MultipleSelectorRef {
  selectedValue: Option[];
  trigger: HTMLButtonElement | null;
}

const EMPTY_OPTIONS: Option[] = [];

export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

function transToGroupOption(options: Option[], groupBy?: string) {
  if (options.length === 0) {
    return {};
  }

  if (!groupBy) {
    return {
      "": options,
    };
  }

  const groupOption: GroupOption = {};

  options.forEach((option) => {
    const key = (option[groupBy] as string) || "";

    if (!groupOption[key]) {
      groupOption[key] = [];
    }

    groupOption[key].push(option);
  });

  return groupOption;
}

function getUniqueValues(values: string[]) {
  return [...new Set(values)];
}

const MultipleSelector = React.forwardRef<
  MultipleSelectorRef,
  MultipleSelectorProps
>(
  (
    {
      value,
      onChange,
      placeholder = "Select options",
      defaultOptions = EMPTY_OPTIONS,
      options: controlledOptions,
      onSearch,
      loadingIndicator,
      emptyIndicator,
      maxSelected = Number.MAX_SAFE_INTEGER,
      onMaxSelected,
      disabled,
      groupBy,
      className,
      badgeClassName,
      triggerSearchOnFocus = false,
      commandProps,
      inputProps,
    }: MultipleSelectorProps,
    ref: React.Ref<MultipleSelectorRef>,
  ) => {
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const [open, setOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [selected, setSelected] = React.useState<Option[]>(value ?? []);
    const [loadedOptions, setLoadedOptions] =
      React.useState<Option[]>(defaultOptions);

    const availableOptions = controlledOptions ?? loadedOptions;
    const groupedOptions = React.useMemo(
      () => transToGroupOption(availableOptions, groupBy),
      [availableOptions, groupBy],
    );
    const optionByValue = React.useMemo(() => {
      const optionsByValue = new Map<string, Option>();

      for (const option of availableOptions) {
        optionsByValue.set(option.value, option);
      }

      for (const option of selected) {
        if (!optionsByValue.has(option.value)) {
          optionsByValue.set(option.value, option);
        }
      }

      return optionsByValue;
    }, [availableOptions, selected]);
    const selectedValues = React.useMemo(
      () => selected.map(({ value }) => value),
      [selected],
    );
    const selectItems = React.useMemo(
      () =>
        availableOptions.map(({ label, value }) => ({
          label,
          value,
        })),
      [availableOptions],
    );
    const groups = React.useMemo(
      () =>
        Object.entries(groupedOptions).filter(([, options]) => options.length),
      [groupedOptions],
    );

    React.useImperativeHandle(
      ref,
      () => ({
        selectedValue: [...selected],
        trigger: triggerRef.current,
      }),
      [selected],
    );

    React.useEffect(() => {
      if (value !== undefined) {
        setSelected(value);
      }
    }, [value]);

    React.useEffect(() => {
      if (!controlledOptions && !onSearch) {
        setLoadedOptions(defaultOptions);
      }
    }, [controlledOptions, defaultOptions, onSearch]);

    React.useEffect(() => {
      if (!open || !onSearch || !triggerSearchOnFocus) {
        return;
      }

      let cancelled = false;

      const loadOptions = async () => {
        setIsLoading(true);

        try {
          const nextOptions = await onSearch("");

          if (!cancelled) {
            setLoadedOptions(nextOptions);
          }
        } finally {
          if (!cancelled) {
            setIsLoading(false);
          }
        }
      };

      void loadOptions();

      return () => {
        cancelled = true;
      };
    }, [onSearch, open, triggerSearchOnFocus]);

    const applySelection = React.useCallback(
      (nextValues: string[]) => {
        const fixedValues = selected
          .filter((option) => option.fixed)
          .map((option) => option.value);
        const nextValueList = getUniqueValues([...fixedValues, ...nextValues]);

        if (nextValueList.length > maxSelected) {
          onMaxSelected?.(maxSelected);
          return;
        }

        const nextSelected = nextValueList.map(
          (optionValue) =>
            optionByValue.get(optionValue) ?? {
              label: optionValue,
              value: optionValue,
            },
        );

        setSelected(nextSelected);
        onChange?.(nextSelected);
      },
      [maxSelected, onChange, onMaxSelected, optionByValue, selected],
    );

    return (
      <Select<string, true>
        multiple
        disabled={disabled}
        drawerTitle={placeholder}
        items={selectItems}
        open={open}
        onOpenChange={setOpen}
        value={selectedValues}
        onValueChange={applySelection}
      >
        <SelectTrigger
          ref={triggerRef}
          className={cn(
            `
              h-auto min-h-9 px-3 py-1.5 text-left whitespace-normal
              [&>span]:line-clamp-none! [&>span]:flex!
              [&>span]:overflow-visible! [&>span]:whitespace-normal!
            `,
            commandProps?.className,
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {selected.length > 0 ? (
              selected.map((option) => (
                <span
                  key={option.value}
                  className={cn(
                    badgeClassName,
                    `
                      inline-flex max-w-full min-w-0 items-center rounded-full
                      border border-border-light/80 bg-background/80 px-2
                      py-0.5 text-xs/5 text-muted-foreground
                    `,
                  )}
                  data-disabled={disabled}
                  data-fixed={option.fixed}
                >
                  <span className="truncate">{option.label}</span>
                </span>
              ))
            ) : (
              <span
                className={cn(
                  "truncate text-muted-foreground",
                  inputProps?.className,
                )}
              >
                {placeholder}
              </span>
            )}
          </span>
        </SelectTrigger>
        <SelectContent
          align="start"
          className="md:max-h-[min(420px,60vh)]"
          drawerTitle={placeholder}
        >
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {loadingIndicator}
            </div>
          ) : groups.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {emptyIndicator ?? "No results found."}
            </div>
          ) : (
            groups.map(([group, options]) => (
              <SelectGroup key={group || "options"}>
                {group ? <SelectLabel>{group}</SelectLabel> : null}
                {options.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={
                      option.disable ||
                      (option.fixed && selectedValues.includes(option.value))
                    }
                    className="wrap-break-word whitespace-normal"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))
          )}
        </SelectContent>
      </Select>
    );
  },
);

MultipleSelector.displayName = "MultipleSelector";

export default MultipleSelector;
